import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import SEOHead from '../../components/base/SEOHead';

export default function HelpPage() {
  const [activeCategory, setActiveCategory] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '帮助与故障排除',
    description: 'OpenClaw 常见问题解答、故障排除指南和社区支持资源',
    url: `${siteUrl}/help`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  const faqs = [
    {
      question: 'OpenClaw 是什么？',
      answer: 'OpenClaw 是一个开源的 AI 智能体框架，让你可以在各种消息平台上部署自己的 AI 助手。它支持多种 AI 模型提供商，可以集成到 Telegram、WhatsApp、Discord、微信、钉钉、飞书等平台。'
    },
    {
      question: '如何开始使用 OpenClaw？',
      answer: '最简单的方式是使用一键安装脚本：curl -fsSL https://openclaw.ai/install.sh | bash。安装完成后运行 openclaw onboard 进行初始化配置。详细步骤请查看快速开始页面。'
    },
    {
      question: 'OpenClaw 支持哪些 AI 模型？',
      answer: 'OpenClaw 支持多种主流 AI 模型，包括：OpenAI GPT-4、Anthropic Claude、智谱 GLM、通义千问、文心一言、Moonshot、以及本地运行的 Ollama 模型等。你可以同时配置多个模型提供商。'
    },
    {
      question: '如何配置消息渠道？',
      answer: '运行 openclaw channels pair 命令，然后按照提示选择要配置的渠道（如 Telegram、WhatsApp 等）。每个渠道都有详细的配置指南，通常需要创建 Bot 或获取 API 密钥。'
    },
    {
      question: 'OpenClaw 是免费的吗？',
      answer: 'OpenClaw 本身是完全免费和开源的（MIT 许可证）。但使用 AI 模型需要向模型提供商付费（如 OpenAI、Anthropic 等）。你也可以使用免费的本地模型（Ollama）或免费额度的云服务。'
    },
    {
      question: '可以在哪些平台上运行 OpenClaw？',
      answer: 'OpenClaw 支持 macOS、Linux、Windows（通过 WSL2）、Docker 容器、以及各种云平台（AWS、GCP、DigitalOcean 等）。甚至可以在树莓派上运行。'
    },
    {
      question: '如何保护 API 密钥安全？',
      answer: '建议将 API 密钥存储在环境变量或配置文件中，不要提交到代码仓库。使用 .gitignore 忽略配置文件。如果使用网关，启用认证和限流功能。定期轮换密钥。'
    },
    {
      question: 'OpenClaw 支持中文吗？',
      answer: '完全支持！OpenClaw 对中文有很好的支持，特别是使用国内模型（如智谱 GLM、通义千问）时。文档也提供中文版本。'
    },
    {
      question: '遇到问题如何获取帮助？',
      answer: '你可以：1) 查看官方文档和 FAQ；2) 在 GitHub Issues 提问；3) 加入微信交流群；4) 查看故障排除指南；5) 运行 openclaw doctor 诊断问题。'
    },
    {
      question: '如何更新 OpenClaw？',
      answer: '如果使用 npm 安装：npm update -g openclaw。如果使用安装脚本：重新运行安装脚本。如果使用 Docker：docker pull openclaw/openclaw:latest。更新前建议备份配置文件。'
    },
    {
      question: 'OpenClaw 可以用于商业项目吗？',
      answer: '可以！OpenClaw 使用 MIT 许可证，允许商业使用。但请注意遵守所使用的 AI 模型提供商的服务条款。'
    },
    {
      question: '如何自定义智能体的行为？',
      answer: '编辑配置文件中的 system_prompt 字段，定义智能体的角色和行为规则。你还可以启用或禁用特定工具，配置记忆功能，设置多智能体路由等。'
    }
  ];

  const troubleshooting = [
    {
      problem: '安装失败：找不到 Node.js',
      solution: '确保已安装 Node.js 18 或更高版本。运行 node --version 检查。如果未安装，访问 nodejs.org 下载安装。',
      commands: ['node --version', 'npm --version']
    },
    {
      problem: '模型 API 调用失败',
      solution: '检查 API 密钥是否正确，是否有足够的额度。运行 openclaw models test <model> 测试连接。查看日志文件获取详细错误信息。',
      commands: ['openclaw models test anthropic', 'tail -f ~/.openclaw/logs/openclaw.log']
    },
    {
      problem: '渠道配对失败',
      solution: '确保网络连接正常，Bot Token 正确。某些渠道需要特定的网络环境。检查防火墙设置。',
      commands: ['openclaw channels status', 'openclaw doctor']
    },
    {
      problem: '网关无法启动',
      solution: '检查端口是否被占用，配置文件是否正确。尝试使用不同的端口。查看网关日志。',
      commands: ['openclaw gateway start --port 3001', 'lsof -i :3000']
    },
    {
      problem: '内存占用过高',
      solution: '清理旧的会话数据，限制会话历史长度。考虑使用更小的模型或启用会话压缩功能。',
      commands: ['openclaw sessions clear --all', 'openclaw config set memory.max_messages 50']
    },
    {
      problem: 'Docker 容器无法访问',
      solution: '检查端口映射是否正确，防火墙规则是否允许。确保容器正在运行。',
      commands: ['docker ps', 'docker logs openclaw', 'docker port openclaw']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <SEOHead
        title="帮助与故障排除 | OpenClaw 常见问题解答"
        description="OpenClaw 常见问题解答、故障排除指南和社区支持资源 - 快速解决使用中的问题"
        keywords="OpenClaw帮助, 常见问题, 故障排除, 技术支持, 社区支持"
        canonical={`${siteUrl}/help`}
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-10">
          {/* Hero Section */}
          <AnimatedSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-6">
              <i className="ri-question-answer-line text-orange-400"></i>
              <span className="text-sm text-orange-400 font-medium">帮助中心</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              帮助与支持
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              查找常见问题解答、故障排除指南和社区支持资源
            </p>
          </AnimatedSection>

          {/* Category Tabs */}
          <AnimatedSection className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { id: 'faq', label: '常见问题', icon: 'ri-questionnaire-line' },
                { id: 'troubleshooting', label: '故障排除', icon: 'ri-tools-line' },
                { id: 'community', label: '社区支持', icon: 'ri-team-line' },
                { id: 'contact', label: '联系我们', icon: 'ri-mail-line' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap ${
                    activeCategory === tab.id
                      ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/25'
                      : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white border border-slate-700/50'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* FAQ Section */}
          {activeCategory === 'faq' && (
            <AnimatedSection>
              <div className="max-w-5xl mx-auto space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left"
                    >
                      <span className="text-lg font-semibold text-white pr-4">{faq.question}</span>
                      <i className={`ri-arrow-${expandedFaq === idx ? 'up' : 'down'}-s-line text-2xl text-orange-400 flex-shrink-0`}></i>
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-6 pb-5 text-slate-300 leading-relaxed border-t border-slate-800/50 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}

          {/* Troubleshooting Section */}
          {activeCategory === 'troubleshooting' && (
            <AnimatedSection>
              <div className="space-y-6">
                {troubleshooting.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-orange-500/30 transition-all"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line text-red-400 text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">{item.problem}</h3>
                        <p className="text-slate-300 leading-relaxed">{item.solution}</p>
                      </div>
                    </div>

                    {item.commands && item.commands.length > 0 && (
                      <div className="mt-4">
                        <div className="text-sm text-slate-500 mb-2">相关命令</div>
                        <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800 space-y-2">
                          {item.commands.map((cmd, cmdIdx) => (
                            <div key={cmdIdx} className="flex items-center gap-2">
                              <code className="text-sm text-cyan-400 font-mono flex-1">{cmd}</code>
                              <button className="text-slate-500 hover:text-slate-300 transition-colors">
                                <i className="ri-file-copy-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Quick Diagnostic */}
                <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-stethoscope-line text-orange-400"></i>
                    快速诊断
                  </h2>
                  <p className="text-slate-400 mb-6">
                    运行诊断命令自动检测和修复常见问题
                  </p>
                  <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                    <code className="text-cyan-400 font-mono">openclaw doctor --fix</code>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* Community Section */}
          {activeCategory === 'community' && (
            <AnimatedSection>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-8 hover:border-orange-500/30 transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-wechat-line text-3xl text-green-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">微信交流群</h3>
                  <p className="text-slate-400 mb-6">
                    加入微信群与其他用户交流经验，获取实时帮助和最新动态
                  </p>
                  <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                    <img 
                      src="https://static.readdy.ai/image/335e3e62ea46a4ee8fffcd870552cca1/c25c2cb1e24f39a307981851ee478810.jpeg" 
                      alt="微信群二维码" 
                      className="w-48 h-48 mx-auto"
                    />
                    <p className="text-center text-sm text-slate-500 mt-3">扫码加入微信群</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <a
                    href="https://github.com/openclaw/openclaw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-orange-500/30 transition-all"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                        <i className="ri-github-fill text-2xl text-purple-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">GitHub</h3>
                        <p className="text-sm text-slate-400">提交 Issue 和 PR</p>
                      </div>
                    </div>
                    <p className="text-slate-300">
                      在 GitHub 上报告 Bug、提出功能建议或贡献代码
                    </p>
                  </a>

                  <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                        <i className="ri-book-2-line text-2xl text-blue-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">官方文档</h3>
                        <p className="text-sm text-slate-400">完整的使用指南</p>
                      </div>
                    </div>
                    <p className="text-slate-300 mb-4">
                      查看详细的安装、配置和使用文档
                    </p>
                    <a
                      href="https://openclaw.cc"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <span>访问文档</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>

                  <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                        <i className="ri-discuss-line text-2xl text-cyan-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">社区论坛</h3>
                        <p className="text-sm text-slate-400">讨论和分享</p>
                      </div>
                    </div>
                    <p className="text-slate-300">
                      在社区论坛分享使用经验、最佳实践和创意想法
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* Contact Section */}
          {activeCategory === 'contact' && (
            <AnimatedSection>
              <div className="max-w-5xl mx-auto">
                <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 mb-8">
                  <h2 className="text-2xl font-bold text-white mb-6">联系方式</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-mail-line text-2xl text-orange-400 mt-1"></i>
                        <div>
                          <div className="text-white font-medium mb-1">电子邮件</div>
                          <a href="mailto:support@openclaw.ai" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                            support@openclaw.ai
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <i className="ri-github-fill text-2xl text-orange-400 mt-1"></i>
                        <div>
                          <div className="text-white font-medium mb-1">GitHub</div>
                          <a 
                            href="https://github.com/openclaw/openclaw" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            github.com/openclaw/openclaw
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-global-line text-2xl text-orange-400 mt-1"></i>
                        <div>
                          <div className="text-white font-medium mb-1">官方网站</div>
                          <a 
                            href="https://openclaw.cc" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            openclaw.cc
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <i className="ri-time-line text-2xl text-orange-400 mt-1"></i>
                        <div>
                          <div className="text-white font-medium mb-1">响应时间</div>
                          <div className="text-slate-400">通常在 24-48 小时内回复</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Resources */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-6 text-center">
                    <i className="ri-book-open-line text-4xl text-blue-400 mb-3"></i>
                    <h3 className="text-lg font-bold text-white mb-2">文档中心</h3>
                    <p className="text-sm text-slate-400">完整的使用文档和教程</p>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-6 text-center">
                    <i className="ri-video-line text-4xl text-purple-400 mb-3"></i>
                    <h3 className="text-lg font-bold text-white mb-2">视频教程</h3>
                    <p className="text-sm text-slate-400">观看视频快速上手</p>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-6 text-center">
                    <i className="ri-code-s-slash-line text-4xl text-cyan-400 mb-3"></i>
                    <h3 className="text-lg font-bold text-white mb-2">示例代码</h3>
                    <p className="text-sm text-slate-400">参考示例快速开发</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}